import React, { useState, useEffect } from 'react';
import { workbenchApi, jobsApi, applicationsApi, getErrorMessage } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { 
  Briefcase, 
  Users, 
  MessageSquare, 
  Search, 
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  Clock,
  User,
  FileText,
  CheckCircle,
  XCircle,
  Loader2,
  ArrowRight,
  Archive,
  Save
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import WorkbenchEditCandidateForm from '../components/WorkbenchEditCandidateForm';

// Predefined candidate statuses
const CANDIDATE_STATUSES = [
  'Not Responding',
  'Switch Off',
  'Call Back',
  'Number Busy',
  'Not Selected',
  'Not Interested',
  'Recently got a job',
  'Not Reachable',
  'Invalid No',
  'Looking for WFH',
  'Call Disconnected',
  'Selected'
];

// Rejection statuses - these will trigger removal from workbench
const REJECTION_STATUSES = [
  'Not Selected',
  'Not Interested',
  'Recently got a job',
  'Invalid No'
];

const Workbench = () => {
  const { user } = useAuth();
  const [myCandidates, setMyCandidates] = useState([]);
  // Bucket counts come back from the backend on every list call so the tab
  // strip can show {Active 12 · Follow-up 4 · Selected 1} without three
  // separate round-trips.
  const [bucketCounts, setBucketCounts] = useState({ active: 0, follow_up: 0, pipeline_ready: 0, all: 0 });
  const [activeBucket, setActiveBucket] = useState('active');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateRemarks, setCandidateRemarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [remarksLoading, setRemarksLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  
  // Dialog states
  const [isRemarkDialogOpen, setIsRemarkDialogOpen] = useState(false);
  const [isViewRemarksOpen, setIsViewRemarksOpen] = useState(false);
  const [isPipelineDialogOpen, setIsPipelineDialogOpen] = useState(false);
  const [isEditCandidateOpen, setIsEditCandidateOpen] = useState(false);
  const [editingRemark, setEditingRemark] = useState(null);
  
  // Form state
  const [remarkForm, setRemarkForm] = useState({
    remark: '',
    status: '',
    isPrivate: true
  });
  const [formLoading, setFormLoading] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState('');
  
  // Edit candidate form state
  const [editCandidateForm, setEditCandidateForm] = useState({
    name: '',
    email: '',
    current_company: '',
    current_designation: '',
    current_location: '',
    experience_years: '',
    skills: '',
    education: '',
    current_ctc: '',
    expected_ctc: '',
    notice_period: '',
    notes: ''
  });
  
  // Search
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchMyCandidates();
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBucket]);

  const fetchMyCandidates = async () => {
    setLoading(true);
    try {
      const response = await workbenchApi.getMyCandidates(activeBucket);
      // Backend now returns { items, bucket_counts } — keep state shape simple
      // by storing items as the visible array and counts separately.
      const payload = response.data || {};
      const items = Array.isArray(payload) ? payload : (payload.items || []);
      setMyCandidates(items);
      if (!Array.isArray(payload) && payload.bucket_counts) {
        setBucketCounts(payload.bucket_counts);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load workbench'));
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      // Fetch both published and assigned jobs (jobs that recruiters can add candidates to)
      const response = await jobsApi.getAll({ status: 'published,assigned' });
      const jobsList = response.data.items || response.data || [];
      setJobs(jobsList);
      console.log('Fetched jobs for pipeline:', jobsList.length);
    } catch (error) {
      console.error('Failed to load jobs:', error);
      toast.error('Failed to load jobs for pipeline selection');
    }
  };

  const fetchCandidateRemarks = async (candidateId) => {
    setRemarksLoading(true);
    try {
      const response = await workbenchApi.getCandidateRemarks(candidateId);
      setCandidateRemarks(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load remarks'));
    } finally {
      setRemarksLoading(false);
    }
  };

  const handleOpenAddRemark = (candidate) => {
    setSelectedCandidate(candidate);
    setRemarkForm({ remark: '', status: '', isPrivate: true });
    setEditingRemark(null);
    setIsRemarkDialogOpen(true);
  };

  const handleOpenEditRemark = (candidate, remark) => {
    setSelectedCandidate(candidate);
    setRemarkForm({ remark: remark.remark, status: remark.status || '', isPrivate: remark.is_private });
    setEditingRemark(remark);
    setIsRemarkDialogOpen(true);
  };

  const handleOpenViewRemarks = async (candidate) => {
    setSelectedCandidate(candidate);
    await fetchCandidateRemarks(candidate.id);
    setIsViewRemarksOpen(true);
  };

  const handleSaveRemark = async () => {
    if (!remarkForm.status) {
      toast.error('Please select a status');
      return;
    }

    // Combine status and remark
    const fullRemark = remarkForm.remark.trim() 
      ? `[${remarkForm.status}] ${remarkForm.remark}`
      : `[${remarkForm.status}]`;

    setFormLoading(true);
    try {
      if (editingRemark) {
        await workbenchApi.updateRemark(
          editingRemark.id, 
          selectedCandidate.id, 
          fullRemark, 
          remarkForm.isPrivate,
          remarkForm.status
        );
        toast.success('Remark updated successfully');
      } else {
        const res = await workbenchApi.addRemark(
          selectedCandidate.id,
          fullRemark,
          remarkForm.isPrivate,
          remarkForm.status
        );
        // Backend auto-archives "closed" statuses (Not Interested / Not
        // Selected / Recently got a job / Invalid No / Looking for WFH).
        // Surface the action so the recruiter doesn't wonder where the row
        // went after the list refresh.
        if (res?.data?.auto_archived) {
          toast.success(`Saved · ${selectedCandidate?.name || 'Candidate'} archived from workbench (${remarkForm.status})`);
        } else if (res?.data?.bucket === 'follow_up') {
          toast.success(`Saved · moved to Follow-up (${remarkForm.status})`);
        } else if (res?.data?.bucket === 'pipeline_ready') {
          toast.success('Saved · ready to add to Pipeline');
        } else {
          toast.success('Remark added successfully');
        }
      }
      setIsRemarkDialogOpen(false);
      fetchMyCandidates();
      if (isViewRemarksOpen) {
        fetchCandidateRemarks(selectedCandidate.id);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save remark'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteRemark = async (remarkId) => {
    if (!window.confirm('Are you sure you want to delete this remark?')) return;
    
    try {
      await workbenchApi.deleteRemark(remarkId);
      toast.success('Remark deleted successfully');
      fetchMyCandidates();
      if (isViewRemarksOpen && selectedCandidate) {
        fetchCandidateRemarks(selectedCandidate.id);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete remark'));
    }
  };

  // Move candidate to Pipeline
  const handleMoveToPipeline = (candidate) => {
    setSelectedCandidate(candidate);
    setSelectedJobId('');
    setIsPipelineDialogOpen(true);
  };

  const handleConfirmMoveToPipeline = async () => {
    if (!selectedJobId) {
      toast.error('Please select a job');
      return;
    }

    setFormLoading(true);
    try {
      await applicationsApi.create({
        job_id: selectedJobId,
        candidate_id: selectedCandidate.id,
        stage: 'sourcing'
      });
      toast.success(`${selectedCandidate.name || 'Candidate'} moved to pipeline!`);
      setIsPipelineDialogOpen(false);
      fetchMyCandidates();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to move to pipeline'));
    } finally {
      setFormLoading(false);
    }
  };

  // Remove candidate from workbench (for rejected candidates)
  const handleRemoveFromWorkbench = async (candidate) => {
    const latestRemark = candidateRemarks[0];
    const status = latestRemark?.status || getLatestStatus(candidate);
    
    if (!window.confirm(`Remove ${candidate.name || 'this candidate'} from your workbench?\n\nStatus: ${status}\n\nNote: The remark history will be preserved for reporting.`)) {
      return;
    }

    try {
      await workbenchApi.removeCandidate(candidate.id);
      toast.success('Candidate removed from workbench');
      fetchMyCandidates();
      setIsViewRemarksOpen(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to remove candidate'));
    }
  };

  // Open edit candidate dialog
  const handleOpenEditCandidate = (candidate) => {
    setSelectedCandidate(candidate);
    setEditCandidateForm({
      name: candidate.name || '',
      email: candidate.email || '',
      current_company: candidate.current_company || '',
      current_designation: candidate.current_designation || '',
      current_location: candidate.current_location || '',
      experience_years: candidate.experience_years || '',
      skills: Array.isArray(candidate.skills) ? candidate.skills.join(', ') : (candidate.skills || ''),
      education: candidate.education || '',
      current_ctc: candidate.current_ctc || '',
      expected_ctc: candidate.expected_ctc || '',
      notice_period: candidate.notice_period || '',
      notes: candidate.notes || ''
    });
    setIsEditCandidateOpen(true);
  };

  // Save edited candidate
  const handleSaveCandidate = async () => {
    if (!selectedCandidate) return;
    
    setFormLoading(true);
    try {
      // Build update data - only include changed fields
      const updateData = {};
      
      if (editCandidateForm.name && editCandidateForm.name !== selectedCandidate.name) {
        updateData.name = editCandidateForm.name;
      }
      if (editCandidateForm.email && editCandidateForm.email !== selectedCandidate.email) {
        updateData.email = editCandidateForm.email;
      }
      if (editCandidateForm.current_company !== (selectedCandidate.current_company || '')) {
        updateData.current_company = editCandidateForm.current_company || null;
      }
      if (editCandidateForm.current_designation !== (selectedCandidate.current_designation || '')) {
        updateData.current_designation = editCandidateForm.current_designation || null;
      }
      if (editCandidateForm.current_location !== (selectedCandidate.current_location || '')) {
        updateData.current_location = editCandidateForm.current_location || null;
      }
      if (editCandidateForm.experience_years !== '' && editCandidateForm.experience_years !== selectedCandidate.experience_years) {
        updateData.experience_years = parseFloat(editCandidateForm.experience_years) || 0;
      }
      if (editCandidateForm.education !== (selectedCandidate.education || '')) {
        updateData.education = editCandidateForm.education || null;
      }
      if (editCandidateForm.current_ctc !== '' && editCandidateForm.current_ctc !== selectedCandidate.current_ctc) {
        updateData.current_ctc = parseFloat(editCandidateForm.current_ctc) || null;
      }
      if (editCandidateForm.expected_ctc !== '' && editCandidateForm.expected_ctc !== selectedCandidate.expected_ctc) {
        updateData.expected_ctc = parseFloat(editCandidateForm.expected_ctc) || null;
      }
      if (editCandidateForm.notice_period !== '' && editCandidateForm.notice_period !== selectedCandidate.notice_period) {
        updateData.notice_period = parseInt(editCandidateForm.notice_period) || null;
      }
      if (editCandidateForm.notes !== (selectedCandidate.notes || '')) {
        updateData.notes = editCandidateForm.notes || null;
      }
      
      // Handle skills separately
      const newSkills = editCandidateForm.skills
        ? editCandidateForm.skills.split(',').map(s => s.trim()).filter(s => s)
        : [];
      const oldSkills = selectedCandidate.skills || [];
      if (JSON.stringify(newSkills) !== JSON.stringify(oldSkills)) {
        updateData.skills = newSkills;
      }
      
      if (Object.keys(updateData).length === 0) {
        toast.info('No changes to save');
        setIsEditCandidateOpen(false);
        return;
      }
      
      await workbenchApi.updateCandidate(selectedCandidate.id, updateData);
      toast.success('Candidate updated successfully');
      setIsEditCandidateOpen(false);
      fetchMyCandidates();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update candidate'));
    } finally {
      setFormLoading(false);
    }
  };

  // Get the latest status from candidate remarks
  const getLatestStatus = (candidate) => {
    // Check if status is embedded in any of the candidate's data
    const remarkMatch = myCandidates.find(c => c.id === candidate.id);
    return remarkMatch?.latest_status || 'Unknown';
  };

  // Check if candidate status allows moving to pipeline
  const canMoveToPipeline = (candidate) => {
    // Can move if status is "Selected" and not already in pipeline
    const remarks = candidateRemarks.filter(r => r.candidate_id === candidate.id);
    const latestStatus = remarks[0]?.status;
    return latestStatus === 'Selected' && !candidate.pipeline_stage;
  };

  // Check if candidate should be removed (rejection status)
  const shouldRemove = (status) => {
    return REJECTION_STATUSES.includes(status);
  };

  const getStageColor = (stage) => {
    if (!stage) return 'bg-slate-100 text-slate-700';
    const colors = {
      sourced: 'bg-blue-100 text-blue-700',
      screening: 'bg-yellow-100 text-yellow-700',
      interview: 'bg-purple-100 text-purple-700',
      offer: 'bg-green-100 text-green-700',
      joined: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
    };
    return colors[stage] || 'bg-slate-100 text-slate-700';
  };

  const filteredCandidates = myCandidates.filter(c => 
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.current_company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            My Workbench
          </h1>
          <p className="text-slate-600 mt-1">Manage your candidates and add personal remarks</p>
        </div>
      </div>

      {/* Bucket tabs — Active / Follow-up / Selected. Counts come back on
          every list call, so switching tabs only fires one request and the
          numbers stay in sync. "Closed" statuses are auto-archived on the
          backend so they intentionally never appear here. */}
      <div className="flex flex-wrap items-center gap-2" data-testid="workbench-bucket-tabs">
        {[
          { key: 'active', label: 'Active', tone: 'bg-teal-600 text-white', toneInactive: 'bg-white text-slate-700 border-slate-200', hint: 'Needs your attention now' },
          { key: 'follow_up', label: 'Follow-up', tone: 'bg-amber-500 text-white', toneInactive: 'bg-white text-slate-700 border-slate-200', hint: "Couldn't reach — retry later" },
          { key: 'pipeline_ready', label: 'Ready for Pipeline', tone: 'bg-emerald-600 text-white', toneInactive: 'bg-white text-slate-700 border-slate-200', hint: 'Marked Selected — push to a job' },
          { key: 'all', label: 'All', tone: 'bg-slate-700 text-white', toneInactive: 'bg-white text-slate-700 border-slate-200', hint: 'Every non-archived candidate' },
        ].map((t) => {
          const isActive = activeBucket === t.key;
          const count = bucketCounts?.[t.key] ?? 0;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveBucket(t.key)}
              data-testid={`workbench-tab-${t.key}`}
              title={t.hint}
              className={`rounded-full px-4 py-1.5 text-sm font-medium border transition ${
                isActive ? `${t.tone} border-transparent shadow-sm` : `${t.toneInactive} hover:border-slate-300`
              }`}
            >
              {t.label}
              <span className={`ml-2 inline-flex items-center justify-center min-w-[1.5rem] px-1.5 rounded-full text-xs ${
                isActive ? 'bg-white/20' : 'bg-slate-100 text-slate-600'
              }`}>{count}</span>
            </button>
          );
        })}
        <span className="text-xs text-slate-400 ml-1" data-testid="workbench-closed-hint">
          Tip: "Not Interested / Not Selected / Recently got a job / Invalid No / Looking for WFH" auto-archive on save.
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">My Candidates</p>
                <p className="text-2xl font-bold text-slate-900">{myCandidates.length}</p>
              </div>
              <Users className="w-8 h-8 text-teal-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">In Pipeline</p>
                <p className="text-2xl font-bold text-slate-900">
                  {myCandidates.filter(c => c.pipeline_stage).length}
                </p>
              </div>
              <Briefcase className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Remarks</p>
                <p className="text-2xl font-bold text-slate-900">
                  {myCandidates.reduce((acc, c) => acc + (c.remark_count || 0), 0)}
                </p>
              </div>
              <MessageSquare className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Available</p>
                <p className="text-2xl font-bold text-slate-900">
                  {myCandidates.filter(c => !c.pipeline_stage).length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search my candidates..."
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {/* Candidates Table */}
      <Card>
        <CardHeader>
          <CardTitle>My Candidates</CardTitle>
          <CardDescription>Candidates you've added remarks to or added to pipeline</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredCandidates.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No candidates in your workbench yet</p>
              <p className="text-sm text-slate-400 mt-1">Add remarks to candidates or add them to pipeline</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Experience</TableHead>
                  <TableHead>Pipeline Status</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead className="text-center">Remarks</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCandidates.map((candidate) => (
                  <TableRow
                    key={candidate.id}
                    onClick={() => handleOpenViewRemarks(candidate)}
                    className="cursor-pointer hover:bg-slate-50"
                    data-testid={`workbench-row-${candidate.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-teal-700" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 hover:text-teal-700 transition-colors">{candidate.name || 'Unnamed Candidate'}</p>
                          <p className="text-sm text-slate-500">{candidate.email || 'No email'}</p>
                          {candidate.current_company && (
                            <p className="text-xs text-slate-400">
                              {candidate.current_designation || 'N/A'} at {candidate.current_company}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-600">
                        {candidate.experience_years || 0} years
                      </span>
                    </TableCell>
                    <TableCell>
                      {candidate.pipeline_stage ? (
                        <Badge className={getStageColor(candidate.pipeline_stage)}>
                          {candidate.pipeline_stage}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-500">
                          Available
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {candidate.pipeline_job ? (
                        <span className="text-sm text-slate-600">{candidate.pipeline_job}</span>
                      ) : (
                        <span className="text-sm text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                      <Badge variant="outline" className="cursor-pointer" onClick={() => handleOpenViewRemarks(candidate)}>
                        <MessageSquare className="w-3 h-3 mr-1" />
                        {candidate.remark_count || 0}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1 justify-end">
                        {/* On the "Ready for Pipeline" tab, surface a single-
                            click CTA inline so recruiters don't have to open
                            the row's dropdown and hunt for the right action.
                            Hidden once the candidate is already in pipeline. */}
                        {activeBucket === 'pipeline_ready' && !candidate.pipeline_stage && (
                          <Button
                            size="sm"
                            onClick={() => handleMoveToPipeline(candidate)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                            data-testid={`workbench-move-to-pipeline-inline-${candidate.id}`}
                          >
                            <ArrowRight className="w-3.5 h-3.5 mr-1" />
                            Move to Pipeline
                          </Button>
                        )}
                        <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleOpenViewRemarks(candidate)}>
                            <Eye className="w-4 h-4 mr-2" /> View Remarks
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleOpenAddRemark(candidate)}>
                            <Plus className="w-4 h-4 mr-2" /> Add Remark
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleOpenEditCandidate(candidate)}>
                            <Edit className="w-4 h-4 mr-2" /> Edit Candidate
                          </DropdownMenuItem>
                          {!candidate.pipeline_stage && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => handleMoveToPipeline(candidate)}
                                className="text-green-600"
                              >
                                <ArrowRight className="w-4 h-4 mr-2" /> Move to Pipeline
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleRemoveFromWorkbench(candidate)}
                            className="text-red-600"
                          >
                            <Archive className="w-4 h-4 mr-2" /> Remove from Workbench
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Candidate Dialog */}
      <Dialog open={isEditCandidateOpen} onOpenChange={setIsEditCandidateOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Candidate Information</DialogTitle>
            <DialogDescription>
              Update candidate details. Phone number cannot be modified.
            </DialogDescription>
          </DialogHeader>
          <WorkbenchEditCandidateForm
            formData={editCandidateForm}
            setFormData={setEditCandidateForm}
            onSubmit={handleSaveCandidate}
            onCancel={() => setIsEditCandidateOpen(false)}
            formLoading={formLoading}
            disabledPhone={selectedCandidate?.phone}
          />
        </DialogContent>
      </Dialog>

      {/* Move to Pipeline Dialog */}
      <Dialog open={isPipelineDialogOpen} onOpenChange={setIsPipelineDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move to Pipeline</DialogTitle>
            <DialogDescription>
              Select a job to add {selectedCandidate?.name || 'this candidate'} to the pipeline
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Job *</Label>
              {jobs.length === 0 ? (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-700">
                    No active jobs available. Please ensure there are published or assigned jobs in the system.
                  </p>
                </div>
              ) : (
                <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                  <SelectTrigger data-testid="pipeline-job-select">
                    <SelectValue placeholder="Choose a job position" />
                  </SelectTrigger>
                  <SelectContent>
                    {jobs.map((job) => (
                      <SelectItem key={job.id} value={job.id}>
                        {job.title} - {job.client_name || 'No Client'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            {selectedCandidate && (
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-600">
                  <strong>Candidate:</strong> {selectedCandidate.name || 'Unnamed'}
                </p>
                <p className="text-sm text-slate-600">
                  <strong>Experience:</strong> {selectedCandidate.experience_years || 0} years
                </p>
                {selectedCandidate.skills?.length > 0 && (
                  <p className="text-sm text-slate-600">
                    <strong>Skills:</strong> {selectedCandidate.skills.slice(0, 5).join(', ')}
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPipelineDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmMoveToPipeline} 
              disabled={formLoading || !selectedJobId}
              className="bg-green-600 hover:bg-green-700"
            >
              {formLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Moving...
                </>
              ) : (
                <>
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Move to Pipeline
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Remark Dialog */}
      <Dialog open={isRemarkDialogOpen} onOpenChange={setIsRemarkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingRemark ? 'Edit Remark' : 'Add Remark'}</DialogTitle>
            <DialogDescription>
              {selectedCandidate && `For ${selectedCandidate.name || 'this candidate'}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Candidate Status *</Label>
              <Select 
                value={remarkForm.status} 
                onValueChange={(value) => setRemarkForm({ ...remarkForm, status: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {CANDIDATE_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Additional Remarks (Optional)</Label>
              <Textarea
                value={remarkForm.remark}
                onChange={(e) => setRemarkForm({ ...remarkForm, remark: e.target.value })}
                placeholder="Enter additional notes about this candidate..."
                rows={3}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="private"
                checked={remarkForm.isPrivate}
                onCheckedChange={(checked) => setRemarkForm({ ...remarkForm, isPrivate: checked })}
              />
              <Label htmlFor="private" className="text-sm">
                Private (only visible to you)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRemarkDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveRemark} 
              disabled={formLoading || !remarkForm.status}
              className="bg-teal-700 hover:bg-teal-800"
            >
              {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingRemark ? 'Update' : 'Add'} Remark
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Remarks Dialog */}
      <Dialog open={isViewRemarksOpen} onOpenChange={setIsViewRemarksOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Remarks for {selectedCandidate?.name}</DialogTitle>
            <DialogDescription>
              View and manage remarks for this candidate
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Button 
              onClick={() => handleOpenAddRemark(selectedCandidate)}
              className="bg-teal-700 hover:bg-teal-800"
            >
              <Plus className="w-4 h-4 mr-2" /> Add New Remark
            </Button>
            
            {remarksLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-700"></div>
              </div>
            ) : candidateRemarks.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500">No remarks yet</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {candidateRemarks.map((remark) => (
                  <div key={remark.id} className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium text-slate-900">{remark.recruiter_name}</span>
                          {remark.is_private && (
                            <Badge variant="outline" className="text-xs">Private</Badge>
                          )}
                        </div>
                        <p className="text-slate-600">{remark.remark}</p>
                        <div className="flex items-center gap-1 mt-2 text-xs text-slate-400">
                          <Clock className="w-3 h-3" />
                          {format(new Date(remark.created_at), 'MMM dd, yyyy HH:mm')}
                          {remark.updated_at && ' (edited)'}
                        </div>
                      </div>
                      {remark.recruiter_id === user?.id && (
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleOpenEditRemark(selectedCandidate, remark)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteRemark(remark.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Workbench;
